import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type CompensationRequestView, type SupportCompensationAction, type SupportCompensationView } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { AUTO_REFRESH_MS, useAutoRefresh, Button, Field, MoneyInput, Notice, Panel, Select, Table, SectionTitle, Text, FormStack, Strong, Stack, EmptyCell } from '@vello/web-theme';
import { refundAuthority, riderWalletAuthority, ticketReference } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useScopeZone } from './scope-zone';
import { formatMarketInstant as fmtZoned } from '../../src/index';
import { MarketField } from './market-field';

/**
 * Finance › the compensations support asked for (W5-SUPPORT-DESK, D384). The agent moves no money in the cash-only pilot;
 * it flags "compensation requested" on the ticket and Finance reads the flags of a market here
 * (`GET /v1/support/compensation-requests`, `ledger:view`). No person data: the ticket, the reason, who asked and when.
 *
 * W6-REFUND-FOUR-EYES (D398): the flag is no longer a dead end. A Finance operator proposes the REFUND or COUPON it asked
 * for (`POST /v1/support/tickets/:id/compensation-proposal`, `payment:refund`) in the MARKET's currency (the list says
 * which; the amount is typed through the design system's money input, never minor units). Within the governed
 * threshold it is recorded at once; above it it goes to Finance › Aprovações de reembolso for a second person.
 *
 * D833 (D-11): with `rider_wallet:propose` it can also be a credit to the rider's Saldo Vello (the wallet's own four eyes), and every
 * row shows what the flag REALLY became - awaiting a second approval, approved with nothing paid or issued (a refund or coupon intent),
 * issued (the credit moved), rejected or withdrawn - never "executed" for something nobody received.
 *
 * D840 (owner D835 #6): the choice lists only the compensations whose rail exists (the server's `compensationRails`: the Saldo Vello credit
 * today); a refund or coupon is hidden until its rail is built, and one recorded before keeps showing its state.
 */
export function CompensationRequestsPanel(): ReactElement {
  const { runtime, grants } = useAdmin();
  const { t, lang } = useT();
  const { fail, v, refusal: refusalOf } = useVocab();
  const { zone } = useScopeZone(); // D420: instants in the scope's market zone
  const { mayPropose } = refundAuthority(grants);
  const mayCredit = riderWalletAuthority(grants).mayPropose;
  const [market, setMarket] = useState('');
  const [rows, setRows] = useState<readonly CompensationRequestView[] | undefined>(undefined);
  const [currency, setCurrency] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [proposing, setProposing] = useState<CompensationRequestView | undefined>(undefined);
  // D840 (owner D835 #6): only the compensations whose rail exists are offered (the server's list; Saldo Vello alone today). A REFUND or COUPON
  // made before keeps showing its honest state in the table.
  const [rails, setRails] = useState<readonly SupportCompensationAction[]>(['RIDER_CREDIT']);
  const offered = rails.filter((r) => r !== 'RIDER_CREDIT' || mayCredit);
  const [action, setAction] = useState<SupportCompensationAction>('RIDER_CREDIT');
  const [amount, setAmount] = useState<bigint | undefined>(undefined);
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger' | 'warning'; text: string } | undefined>(undefined);

  const money = (m: { readonly amountMinor: string; readonly currency: string }): string =>
    /^\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency) ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' }) : `${m.amountMinor} ${m.currency}`;

  /** The newest compensation of a flag in words: what, how much, and what it really became. */
  const compensationLine = (c: SupportCompensationView | undefined): string =>
    c === undefined ? t('refunds.comp.none') : t('refunds.comp.line', { action: t(`refunds.propose.${c.action}`), amount: money(c.amount), state: t(`refunds.comp.state.${c.state}`) });

  const seq = useRef(0);
  const load = useCallback(
    async (marketId: string, background = false): Promise<void> => {
      const n = ++seq.current;
      // A background read keeps the rows on screen; a new market starts from a clean list.
      if (!background) {
        setError(undefined);
        setRows(undefined);
      }
      if (marketId === '') return;
      const res = await runtime.supportDesk.compensationRequests(marketId);
      if (n !== seq.current) return; // an older market's late answer is dropped
      if (res.ok) {
        setError(undefined);
        setRows(res.value.requests);
        setCurrency(res.value.currency ?? null);
        setRails(res.value.compensationRails ?? ['RIDER_CREDIT']);
      } else {
        setRows(undefined);
        setError(t('desk.fin.error', { code: fail(res.error) }));
      }
    },
    [runtime, t],
  );

  // The flags follow the scoped market by itself and stay current in the background (no "load" button).
  useEffect(() => {
    void load(market.trim());
  }, [market, load]);
  useAutoRefresh(() => load(market.trim(), true), { intervalMs: AUTO_REFRESH_MS.queue, enabled: market.trim() !== '' });

  const propose = async (): Promise<void> => {
    if (proposing === undefined || amount === undefined || amount <= 0n) return;
    setBusy(true);
    setMsg(undefined);
    const res = await runtime.refundApprovals.proposeCompensation(proposing.ticketId, { action, amountMinor: amount.toString(), reasonCode: proposing.reasonCode });
    setBusy(false);
    if (res.ok) {
      // D833: the message follows the REAL state the server answered (a credit that moved, a decision that paid nothing, a queue).
      const c = res.value.compensation;
      const amountText = c !== undefined ? money(c.amount) : res.value.approval !== undefined ? money(res.value.approval.amount) : '';
      const state = c?.state ?? (res.value.approval?.status === 'PENDING' ? 'PROPOSED' : 'APPROVED');
      setMsg({ tone: 'success', text: t(state === 'PROPOSED' ? 'refunds.propose.queued' : state === 'EXECUTED' ? 'refunds.propose.credited' : 'refunds.propose.recorded', { amount: amountText }) });
      setProposing(undefined);
      setAmount(undefined);
      void load(market.trim(), true);
      return;
    }
    setMsg({ tone: 'danger', text: refusalOf('refusal.admin.refund', res.error) ?? t('ui.actionFailed', { code: fail(res.error) }) });
  };

  return (
    <Panel><Stack gap={4}>
      <SectionTitle>{t('desk.fin.title')}</SectionTitle>
      <Text as="p" role="caption" tone="tertiary">{t('desk.fin.hint')}</Text>
      <MarketField id="comp-market" value={market} onChange={setMarket} hintId="fin.field.market.hint" />
      {error !== undefined && <Notice tone="danger">{error}</Notice>}
      {msg !== undefined && (
        <div>
          <Notice tone={msg.tone}>{msg.text}</Notice>
        </div>
      )}
      {rows !== undefined && (
        <Table
          columns={[
            { key: 'ticket', header: t('desk.fin.col.ticket'), render: (r: CompensationRequestView) => <span className="tnum" title={r.ticketId}>{ticketReference(r)}</span> },
            { key: 'reason', header: t('desk.fin.col.reason'), render: (r: CompensationRequestView) => v('support.compensation_reason', r.reasonCode) },
            { key: 'when', header: t('desk.fin.col.when'), render: (r: CompensationRequestView) => <span className="tnum">{fmtZoned(r.requestedAt, zone, lang)}</span> },
            { key: 'status', header: t('desk.fin.col.status'), render: (r: CompensationRequestView) => v('support.ticket_status', r.status) },
            { key: 'outcome', header: t('refunds.comp.col'), render: (r: CompensationRequestView) => <span className="tnum">{compensationLine(r.compensations?.[0])}</span> },
            {
              key: 'propose',
              header: '',
              render: (r: CompensationRequestView) =>
                mayPropose && currency !== null && offered.length > 0 ? (
                  <Button variant="secondary" disabled={busy} onClick={() => { setProposing(r); setAction(offered[0] ?? 'RIDER_CREDIT'); setAmount(undefined); setMsg(undefined); }}>{t('refunds.propose.open')}</Button>
                ) : (
                  <span />
                ),
            },
          ]}
          rows={rows}
          rowKey={(r) => r.ticketId}
          empty={<EmptyCell>{t('desk.fin.empty')}</EmptyCell>}
        />
      )}
      {rows !== undefined && rows.length > 0 && currency === null && mayPropose && (
        <div>
          <Notice tone="warning">{t('refunds.propose.noCurrency')}</Notice>
        </div>
      )}
      {rows !== undefined && rows.length > 0 && mayPropose && offered.length === 0 && (
        <div>
          <Notice tone="neutral">{t('refunds.propose.noRail')}</Notice>
        </div>
      )}
      {proposing !== undefined && currency !== null && (
        <FormStack gap={3}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void propose();
          }}
        >
          <Strong as="h3" role="body">
            {t('refunds.propose.open')} — <span className="tnum">{ticketReference(proposing)}</span>
          </Strong>
          <Field label={t('refunds.propose.action')} htmlFor="comp-action">
            <Select id="comp-action" value={action} onChange={(e) => setAction(offered.find((o) => o === e.target.value) ?? offered[0] ?? 'RIDER_CREDIT')}>
              {offered.map((o) => <option key={o} value={o}>{t(`refunds.propose.${o}`)}</option>)}
            </Select>
          </Field>
          <MoneyInput id="comp-amount" label={t('refunds.propose.amount')} currency={currency} locale={lang} valueMinor={amount} onChangeMinor={setAmount} issueText={(issue) => v('format.money_input_issue', issue)} required />
          <div>
            <Button type="submit" variant="secondary" disabled={busy || amount === undefined || amount <= 0n}>{t('refunds.propose.submit')}</Button>
          </div>
        </FormStack>
      )}
    </Stack></Panel>
  );
}
